<template>
  <div class="h-full overflow-y-auto p-4" style="background: var(--sidebar-bg);">
    <!-- All Domains: Use Tree Component -->
    <div v-if="treeNodes && treeNodes.length > 0">
      <Tree
        :value="treeNodes"
        :expandedKeys="expandedKeys"
        @nodeSelect="onTreeNodeSelect"
        @nodeExpand="onNodeExpand"
        @nodeCollapse="onNodeCollapse"
        :pt="treePassThrough"
        selectionMode="single"
      >
        <template #default="{ node }">
          <div class="flex items-start gap-3 flex-1">
            <span
              class="text-sm flex-1 transition-all duration-200"
              :class="[
                {
                  'cursor-default select-none': !node.selectable,
                  'italic font-medium tracking-wide text-gray-500': !node.selectable && node.icon === 'pi pi-folder',
                  'font-normal': node.selectable,
                  'font-semibold': isActive(node.data)
                },
                isActive(node.data) ? 'text-[#e4aa23]' : 'text-gray-800'
              ]"
            >
              {{ node.label }}
            </span>
          </div>
        </template>
      </Tree>
    </div>

    <div v-else-if="isLoading" class="flex items-center justify-center h-32">
      <ProgressSpinner style="width: 40px; height: 40px" strokeWidth="4" />
    </div>

    <div v-else class="text-center py-8" style="color: var(--text-muted);">
      <i class="pi pi-inbox text-3xl mb-2"></i>
      <p class="text-sm">No sections available</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import ProgressSpinner from 'primevue/progressspinner';
import Tree from 'primevue/tree';
import type { Section, DomainSection, DomainType } from '@/types/ashmatics';

// Define TreeNode interface for PrimeVue Tree component
interface TreeNode {
  key: string;
  label: string;
  icon?: string;
  selectable?: boolean;
  children?: TreeNode[];
  data?: any;
}

const props = defineProps<{
  domain: DomainType;
  sections: DomainSection[];
  activeSection: Section | null;
}>();

const emit = defineEmits<{
  'section-selected': [section: Section];
}>();

// Tree state for Framework domain
const expandedKeys = ref<Record<string, boolean>>({});

const isLoading = computed(() => {
  return props.sections.length === 0;
});

const isActive = (section: Section | null | undefined) => {
  if (!section) return false;
  return props.activeSection?.id === section.id;
};

const selectSection = (section: Section) => {
  emit('section-selected', section);
};

/**
 * Build tree nodes for all domains
 * Structure: Root → Sections → Items
 */
const treeNodes = computed((): TreeNode[] => {
  if (!props.sections.length) {
    return [];
  }

  // Determine root label and icon based on domain
  const domainConfig = {
    framework: { label: 'CHAR Reference Model', icon: 'pi pi-book' },
    policy: { label: 'Policy Domain', icon: 'pi pi-shield' },
    process: { label: 'Process Domain', icon: 'pi pi-sitemap' }
  };

  const config = domainConfig[props.domain];

  // Create root node
  const rootNode: TreeNode = {
    key: `${props.domain}-root`,
    label: config.label,
    icon: config.icon,
    selectable: false,
    children: []
  };

  // Add section nodes
  props.sections.forEach((section) => {
    // If section has only one subsection, add it directly to root (no folder)
    if (section.subsections.length === 1) {
      const singleItem = section.subsections[0];
      const itemNode: TreeNode = {
        key: `item-${singleItem.id}`,
        label: singleItem.title,
        icon: 'pi pi-file',
        selectable: true,
        data: singleItem // Store the actual section data for selection
      };
      rootNode.children?.push(itemNode);
    } else {
      // Multiple subsections: create folder structure
      const sectionNode: TreeNode = {
        key: `section-${section.id}`,
        label: section.title,
        icon: 'pi pi-folder',
        selectable: false,
        children: []
      };

      // Add item nodes (subsections)
      section.subsections.forEach((item) => {
        const itemNode: TreeNode = {
          key: `item-${item.id}`,
          label: item.title,
          icon: 'pi pi-file',
          selectable: true,
          data: item // Store the actual section data for selection
        };

        sectionNode.children?.push(itemNode);
      });

      rootNode.children?.push(sectionNode);
    }
  });

  return [rootNode];
});

/**
 * Handle tree node selection
 * Only emit selection for selectable nodes (items with data)
 */
const onTreeNodeSelect = (node: TreeNode) => {
  // Only process selection if node is explicitly marked as selectable and has data
  if (node.selectable === true && node.data) {
    selectSection(node.data as Section);
  }
  // Silently ignore clicks on non-selectable nodes (root, sections)
};

/**
 * Handle node expansion
 */
const onNodeExpand = (node: TreeNode) => {
  // Prevent collapsing the root node
  if (!node.key.toString().endsWith('-root')) {
    expandedKeys.value[node.key as string] = true;
  }
};

/**
 * Handle node collapse
 */
const onNodeCollapse = (node: TreeNode) => {
  // Prevent collapsing the root node - keep it always expanded
  if (node.key.toString().endsWith('-root')) {
    // Force it to stay expanded
    expandedKeys.value[node.key as string] = true;
    return;
  }
  delete expandedKeys.value[node.key as string];
};

// Auto-expand root node when domain changes and keep it expanded
watch(() => props.domain, (newDomain) => {
  const rootKey = `${newDomain}-root`;
  expandedKeys.value = { [rootKey]: true };
}, { immediate: true });

// Ensure root node stays expanded
watch(() => treeNodes.value, (newTreeNodes) => {
  if (newTreeNodes.length > 0) {
    const rootKey = newTreeNodes[0].key;
    expandedKeys.value[rootKey] = true;
  }
}, { immediate: true });

// PrimeVue PassThrough configuration to replace :deep() selectors
const treePassThrough = {
  root: {
    class: 'border-0 bg-transparent p-0'
  },
  container: {
    class: 'p-0'
  },
  node: {
    class: 'py-2'
  },
  content: ({ instance, props, state }: any) => ({
    class: [
      'py-3 px-2 rounded-md transition-all duration-200 border-0 flex items-center gap-4',
      {
        // Root node styling
        'cursor-default': props.node?.key?.endsWith('-root'),
        // Hover effects for selectable nodes
        'hover:bg-[#e4aa23]/10': props.node?.selectable && !props.node?.key?.endsWith('-root'),
        // Section folder spacing
        'mt-2 mb-1': !props.node?.selectable && props.node?.icon === 'pi pi-folder'
      }
    ]
  }),
  toggler: ({ props }: any) => ({
    class: [
      'text-gray-500 w-8 h-8 mr-1 hover:text-[#e4aa23] transition-colors',
      {
        'invisible pointer-events-none': props.node?.key?.endsWith('-root')
      }
    ]
  }),
  nodeChildren: {
    class: 'pl-4 ml-0'
  },
  nodeIcon: ({ props }: any) => ({
    class: [
      'mr-3 text-sm transition-colors',
      {
        'text-[#8c7851] text-base': !props.node?.selectable && props.node?.icon === 'pi pi-folder',
        'text-gray-400 text-[0.9rem]': props.node?.selectable,
        'text-gray-500': props.node?.key?.endsWith('-root')
      }
    ]
  })
};
</script>

<style scoped>
/* Minimal CSS - most styling moved to PassThrough configuration */

/* Nested indentation for better hierarchy */
:deep(.p-tree-node-children .p-tree-node) {
  @apply pl-6;
}

:deep(.p-tree-node-children .p-tree-node-children .p-tree-node) {
  @apply pl-8;
}

/* Selection highlight */
:deep(.p-tree-node-content.p-highlight) {
  @apply bg-[#e4aa23]/15;
}

/* Root node hover override (PassThrough doesn't handle this specific case) */
:deep(.p-tree-node:first-child > .p-tree-node-content:hover) {
  @apply bg-transparent !important;
}
</style>
